import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Component, effect, inject, input, signal, untracked, viewChild } from '@angular/core';

import { UserService } from '@app/shared/services/user.service';
import { IColumn } from '@app/shared/interceptors/column.interface';
import { SessionService } from '@app/shared/services/session.service';
import { NO_ACTIVITY_DAYS } from '@app/shared/constants/global.constant';
import { toEndOfDay, toStartOfDay } from '@app/shared/utils/date-utils';
import { ISessionResponse } from '@app/shared/interfaces/session.interface';
import { CounterDetailComponent } from '../counter-detail/counter-detail.component';
import { TableComponent } from '@shared/components/table-component/table.component';
import { IGetUsersResponse } from '@app/shared/interfaces/get-users-response.interface';
import { ITableChangeEvent } from '@app/shared/interfaces/table-change-event.interface';
import { DatePickerComponent } from '@app/shared/components/date-picker-component/date-picker-component';

import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { FloatLabelModule } from 'primeng/floatlabel';
import { AutoComplete, AutoCompleteCompleteEvent } from 'primeng/autocomplete';

@Component({
  selector: 'app-archived-counters-table',
  imports: [
    CommonModule,
    FormsModule,
    TableComponent,
    DatePickerComponent,
    SelectModule,
    FloatLabelModule,
    ButtonModule,
    TooltipModule,
    CounterDetailComponent,
    AutoComplete,
  ],
  templateUrl: './archived-counters-table.component.html',
})
export class ArchivedCountersTableComponent {
  sessionService = inject(SessionService);
  userService = inject(UserService);

  tableComponent = viewChild.required(TableComponent);
  counterDetailDialog = viewChild.required(CounterDetailComponent);
  nameColumnTemplate = viewChild.required('nameColumnTemplate');
  statusColumnTemplate = viewChild.required('statusColumnTemplate');
  detailColumnTemplate = viewChild.required('detailColumnTemplate');
  appTypeColumnTemplate = viewChild.required('appTypeColumnTemplate');
  sessionStartDateColumnTemplate = viewChild.required('sessionStartDateColumnTemplate');
  archivedDateColumnTemplate = viewChild.required('archivedDateColumnTemplate');

  selectedPort = input<string>('ALL');

  sessionServiceRespone = signal<any>(null);
  archivedCounterData = signal<any[]>([]);
  tableLoading = signal<boolean>(false);
  // Filter Variables
  searchFilterValue = signal<string>('');
  dateRangeFilter = signal<Date[] | null>([new Date(), new Date()]);
  appTypeList: any[] = [
    { value: 'FALLBACK', label: 'Fallback' },
    { value: 'AGENCY', label: 'DCS' },
  ];
  selectedAppType = signal<string | null>(null);
  userList = signal<IGetUsersResponse['content'] | []>([]);
  selectedUser = signal<string | null>(null);

  noActivityDays = NO_ACTIVITY_DAYS;
  cols: IColumn[] = [];

  constructor() {
    effect(() => {
      const port = this.selectedPort(); // Track changes to the selected port
      this.initTableCols();

      untracked(() => this.onChangeFilter());
    });
  }

  // --- Initialize Operations ---
  initTableCols() {
    this.cols = [
      { key: 'computerName', label: 'Counter Name', template: this.nameColumnTemplate() },
      ...(this.selectedPort() === 'ALL' ? [{ key: 'defPort', label: 'Def Port' }] : []),
      {
        key: 'applicationType',
        label: 'App Type',
        searchable: true,
        template: this.appTypeColumnTemplate(),
      },
      { key: 'userName', label: 'User', display: 'none' },
      { key: 'checkInSystem', label: 'Check-in System', searchable: true },
      {
        key: 'loginTime',
        label: 'Session Start Date | Time',
        template: this.sessionStartDateColumnTemplate(),
      },
      {
        key: 'logoutTime',
        label: 'Archived Date | Time',
        template: this.archivedDateColumnTemplate(),
      },
      {
        key: 'serverPingInformation.Status',
        label: 'Last Status',
        template: this.statusColumnTemplate(),
      },
      { key: 'detail', label: '', template: this.detailColumnTemplate() },
    ];
  }

  // --- API Operations ---
  getSessionsForArchivedCounters() {
    const rowSize = this.tableComponent().rowsPerPage();
    const currentPage = this.tableComponent().currentPage() - 1;
    const loginTime = toStartOfDay(this.dateRangeFilter()?.[0]);
    const logoutTime = toEndOfDay(this.dateRangeFilter()?.[1]);
    const appType = this.selectedAppType() ?? '';
    const username = this.selectedUser() ?? '';
    const computerName = this.searchFilterValue() ?? '';
    const defPort = this.selectedPort() === 'ALL' ? undefined : this.selectedPort();
    this.tableLoading.set(true);

    this.sessionService
      .getSessionsForArchivedCounters(
        currentPage,
        rowSize,
        computerName,
        loginTime,
        logoutTime,
        appType,
        username,
        defPort,
      )
      .subscribe({
        next: (response: ISessionResponse) => {
          const archivedCounterData = response.content;
          this.sessionServiceRespone.set(response);
          this.archivedCounterData.set(archivedCounterData);
          this.tableLoading.set(false);
        },
        error: (error) => {
          console.log(error);
          this.tableLoading.set(false);
        },
      });
  }

  // --- Filter and Pagination Operations ---
  onChangeFilter() {
    this.tableComponent().currentPage.set(1);
    this.getSessionsForArchivedCounters();
  }

  onSearchUser(event: AutoCompleteCompleteEvent) {
    this.userService.getUsers(0, 100, event.query, this.selectedPort()).subscribe({
      next: (response) => {
        const content = response?.content ?? [];
        this.userList.set(content);
      },
      error: (error) => {
        console.error(error);
      },
    });
  }

  onChangeDateRangeFilter(event: Date[] | null) {
    if (event === undefined) {
      this.dateRangeFilter.set(null);
    } else {
      this.dateRangeFilter.set(event);
    }

    this.tableComponent().currentPage.set(1);
    this.getSessionsForArchivedCounters();
  }

  cancelDateRange(event: any) {
    this.dateRangeFilter.set([new Date(), new Date()]);
    this.tableComponent().currentPage.set(1);
    this.getSessionsForArchivedCounters();
  }

  onSearchCounterFilter(event: any) {
    this.searchFilterValue.set(event);
    this.onChangeFilter();
  }

  onChangePage(event: ITableChangeEvent) {
    this.getSessionsForArchivedCounters();
  }

  openDetailDialog(data: any) {
    this.counterDetailDialog().displayDialog.set(true);
    this.counterDetailDialog().selectedItem.set(data);
  }
}
